import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import {
    ColumnDef,
    ColumnWidth,
    combineLatestAlwaysEmit,
    Comparable,
    ComplexMapFieldValue,
    DisplayMode,
    FieldDefinitionHelper,
    getAny,
    LoadingBehaviorSubject,
    NonNullComplexMapFieldValue,
    PaginatedResults,
    PaginationInfo,
    SearchBarMode,
    ShFieldDefinitionService,
    TextCell,
    Trackable
} from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { map } from 'rxjs/operators';
import { WebserviceConfig, WebserviceField } from '../../entities/webservice-config.entity';

type FieldValueColumn = ColumnDef<typeof WebserviceListComponent, ComplexObject>;

class ComplexObject implements Comparable, Trackable {
    data: NonNullComplexMapFieldValue;

    constructor(data: NonNullComplexMapFieldValue) {
        this.data = data;
    }

    equals(item: any): boolean {
        return isEqual(item?.data, this.data);
    }

    trackBy(): string {
        return JSON.stringify(this.data);
    }
}

// @dynamic
@Component({
    selector: 'med-webservice-list',
    templateUrl: './webservice-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebserviceListComponent implements OnInit {
    static TEXT: FieldValueColumn;

    public readonly DEFAULT_DISPLAY_MODE: DisplayMode = DisplayMode.LIST;

    public readonly resultsPerPageOptions: number[] = [5];

    public allowSort = false;
    public showViewToggle = false;
    public searchBarMode: SearchBarMode = SearchBarMode.NONE;
    public isLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    public columns$: Observable<FieldValueColumn[]>;
    public paginatedResults$: ReplaySubject<PaginatedResults<ComplexObject>> = new ReplaySubject<
        PaginatedResults<ComplexObject>
    >();

    @Input() set paginatedResults(paginatedResults: PaginatedResults<NonNullComplexMapFieldValue> | null) {
        if (paginatedResults !== null) {
            const convertedResults: ComplexObject[] = paginatedResults.results.map(item => new ComplexObject(item));
            this.paginatedResults$.next({ ...paginatedResults, results: convertedResults });
        }
    }

    @Input() set isLoading(loading: boolean) {
        this.isLoading$.next(loading);
    }

    @Input() webserviceConfig: WebserviceConfig;

    @Output() paginationChange: EventEmitter<PaginationInfo> = new EventEmitter<PaginationInfo>();
    @Output() selected: EventEmitter<ComplexMapFieldValue[]> = new EventEmitter<ComplexMapFieldValue[]>();

    // Injectors
    private fieldDefinitionService: ShFieldDefinitionService = inject(ShFieldDefinitionService);

    ngOnInit(): void {
        if (this.webserviceConfig) {
            this.generateColumns();
        }
    }

    private generateColumns(): void {
        this.columns$ = combineLatestAlwaysEmit(
            this.webserviceConfig.fields.filter(field => field.ShowColumn).map(field => this.createColumn(field))
        ).pipe(map(columns => columns.filter((column): column is FieldValueColumn => !!column)));
    }

    private createColumn(webserviceField: WebserviceField): Observable<FieldValueColumn | null> {
        const flatKey: string =
            webserviceField.FieldDefinitionDottedKey.split('.').pop() ?? webserviceField.FieldDefinitionDottedKey;

        return this.fieldDefinitionService.getByDottedKey(webserviceField.FieldDefinitionDottedKey).pipe(
            map(fieldDefinition => {
                if (!fieldDefinition) {
                    console.error(
                        `WebserviceListComponent:: Failed to generate column for webservice field ${webserviceField.Key}`
                    );
                    return null;
                }

                const translation: string = fieldDefinition.Label;

                return new ColumnDef({
                    key: 'TEXT',
                    translateLabel: translation,
                    mapper: (object: ComplexObject) => {
                        const isList: boolean = FieldDefinitionHelper.IsList(fieldDefinition);

                        let value: string;

                        if (isList) {
                            const listValue: any = getAny(object.data, flatKey);
                            const childKey: string = Object.keys(listValue)[0];
                            value = listValue[childKey]
                                .filter((childValue: string) => !!childValue && childValue !== '')
                                .join('<br />');
                        } else {
                            value = object.data[flatKey].toString();
                        }

                        return new TextCell(value);
                    },
                    width: ColumnWidth.NORMAL
                });
            })
        );
    }

    public onPaginationChange(pagination: PaginationInfo): void {
        this.paginationChange.next(pagination);
    }

    public onItemsChecked(selected: ComplexObject[]): void {
        this.selected.next(selected.map(item => item.data));
    }
}
